import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ArrowLeftCircle, Plus } from "lucide-react";
import { auth, db } from "./firebase/firebaseConfig";

const Container = styled.div`
  box-sizing: border-box;
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(0, 212, 255, 0.14), transparent 28%),
    radial-gradient(circle at bottom right, rgba(255, 122, 24, 0.1), transparent 26%),
    #0a0d12;
  color: #eef4ff;
  padding: 38px;

  * {
    box-sizing: border-box;
  }

  @media (max-width: 640px) {
    padding: 22px;
  }
`;

const Header = styled.div`
  max-width: 920px;
  margin: 0 auto 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: #f8fbff;
  font-size: 1.85rem;
  font-weight: 800;
`;

const Subtitle = styled.p`
  margin: 8px 0 0;
  color: #a8b3c7;
  font-weight: 500;
`;

const Form = styled.form`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  display: grid;
  gap: 16px;
  background: rgba(12, 18, 28, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 22px;
  box-shadow: 0 24px 45px rgba(0, 0, 0, 0.18);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
  min-width: 0;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 8px;
  min-width: 0;
  color: #a8b3c7;
  font-weight: 700;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 130px;
  resize: vertical;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;
  line-height: 1.5;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  padding: 12px 18px;
  font-weight: 800;
  cursor: pointer;
  color: #071018;
  background: linear-gradient(135deg, #00d4ff, #7dd3fc);
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
`;

const BackButton = styled(Button)`
  color: #d7dee9;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Select = styled.select`
  width: 100%;
  min-width: 0;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const planningTypes = [
  { value: "youtube", label: "YouTube" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "instagram", label: "Instagram" },
  { value: "projects", label: "Projects" },
  { value: "course", label: "Course" },
  { value: "meeting", label: "Meeting" },
  { value: "engineer-codewala-website", label: "Engineer Codewala Website" },
  { value: "engineer-codewala-app", label: "Engineer Codewala App" },
  { value: "saas-platform", label: "SaaS Platform" },
  { value: "seo", label: "SEO" },
  { value: "smo", label: "SMO" },
  { value: "admin-panel", label: "Admin Panel" },
];

const getTodayInput = () => {
  const date = new Date();
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 10);
};

const AddPlanningTask = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    details: "",
    taskDate: getTodayInput(),
    taskTime: "",
    type: "projects",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert("Task title is required");
      return;
    }

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert("Admin session expired. Please login again.");
        navigate("/");
        return;
      }

      await currentUser.getIdToken(true);
      setSaving(true);
      await addDoc(collection(db, "planningTasks"), {
        title: form.title.trim(),
        details: form.details.trim(),
        taskDate: form.taskDate,
        taskTime: form.taskTime || "",
        type: form.type,
        completed: false,
        completedAt: null,
        createdBy: currentUser.uid,
        createdByEmail: currentUser.email || "",
        createdAt: serverTimestamp(),
      });
      alert("Task added");
      navigate("/planning");
    } catch (err) {
      console.error(err);
      alert("Error adding task");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>Add Planning Task</Title>
          <Subtitle>Create a daily task for your planning board.</Subtitle>
        </div>
        <BackButton type="button" onClick={() => navigate("/planning")}>
          <ArrowLeftCircle size={18} /> Back
        </BackButton>
      </Header>

      <Form onSubmit={handleSubmit}>
        <Grid>
          <Field>
            Task Date
            <Input
              type="date"
              name="taskDate"
              value={form.taskDate}
              onChange={handleChange}
            />
          </Field>
          <Field>
            Task Title
            <Input
              name="title"
              placeholder="Today follow up client"
              value={form.title}
              onChange={handleChange}
            />
          </Field>
          <Field>
            Time (Optional)
            <Input
              type="time"
              name="taskTime"
              value={form.taskTime}
              onChange={handleChange}
            />
          </Field>
          <Field>
            Type
            <Select name="type" value={form.type} onChange={handleChange}>
              {planningTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </Field>
        </Grid>
        <Field>
          Details
          <Textarea
            name="details"
            placeholder="Optional notes, links, context..."
            value={form.details}
            onChange={handleChange}
          />
        </Field>

        <ButtonRow>
          <Button type="submit" disabled={saving}>
            <Plus size={18} /> {saving ? "Saving..." : "Add Task"}
          </Button>
          <BackButton type="button" onClick={() => navigate("/planning")}>
            Cancel
          </BackButton>
        </ButtonRow>
      </Form>
    </Container>
  );
};

export default AddPlanningTask;
